/**
 * This is the registration page and is accessed by going to "/user/register".
 *
 * The page displays a form for the user to provide his register information and on submit
 * will make a call to register the user.
 */
import { useMutation } from '@apollo/client';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { signIn } from 'next-auth/react';
import Router, { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Branding } from '~/jobiqo/components/Branding';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import Link from '~/jobiqo/components/Link';
import Messages from '~/jobiqo/components/Messages';
import USER_REGISTER from '~/jobiqo/components/User/Register/graphql/register.gql';
import { AppContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import GET_USER_INFO from '~/jobiqo/graphql/queries/getUser.gql';
import { MessageService, useMessages } from '~/jobiqo/hooks/messages';
import { EVENTS } from '~/jobiqo/lib/analytics';
import { ROLE_APPLICANT, ROLE_RECRUITER } from '~/jobiqo/lib/auth';
import redirect, { sanitizeRedirectUrl } from '~/jobiqo/lib/redirect';
import { getAnonymousUserToken } from '~/jobiqo/lib/session';
import { AppContextInterface } from '~/jobiqo/models/app';
import { getTranslatedLink } from '~/jobiqo/utils/links';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Tab, TabList, TabPanel, Tabs } from '~/vcl/atoms/17-tabs';
import { Box } from '~/vcl/atoms/07-box';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { UserAddIcon } from '~/vcl/icons/UserAdd';
import { HiddenVisually } from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import RichFormattedMessage from '~/jobiqo/components/RichFormattedMessage';
import { ParsedUrlQuery } from 'querystring';
import UserRegisterLayout from './Layout/UserRegisterLayout';
import { getFrontendAssets } from '~/jobiqo/utils/getFrontend';
import { UserRegisterInfoSlot } from '~/jobiqo/components/Slots/UserRegisterInfoSlot';
import { useConfig } from '~/jobiqo/context/config';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { HeaderLoginPagesSlot } from '~/jobiqo/components/Slots/HeaderLoginPagesSlot';
import { useApplyUtmTags } from '~/jobiqo/components/SourceTracking/hooks/useUtmTagging';
import RegistrationForm from '~/jobiqo/components/User/Register/RegistrationForm';
export const ANONYMOUS_REGISTRATION_LS_KEY = 'anonymous_registration_data';
const buildUrl = (
  prefix: string,
  link: string,
  params: URLSearchParams,
  basePath?: string | boolean,
) => {
  const separator = link.includes('?') ? '&' : '?';
  const normalizedBasePath = typeof basePath === 'string' ? basePath : '';

  return `${normalizedBasePath}${prefix}${link}${separator}${params.toString()}`;
};

const onComplete = async (
  data: any,
  isApplicant: any,
  messageService: MessageService,
  currentLanguage?: string,
  defaultLanguage?: string,
  createGdprUserConsent?: any,
  email?: string,
  query?: ParsedUrlQuery,
  resumesActive?: boolean,
  basePath?: string | boolean,
  applyUtmTags?: (query: ParsedUrlQuery, jwt?: string) => Promise<void>,
) => {
  // this is a external redirect (not using the router) we need to make sure we add the
  // appropriate language prefix here.
  const prefix =
    currentLanguage !== defaultLanguage ? `/${currentLanguage}` : '';

  if (data.userRegister && data.userRegister.jwt) {
    const jwt = data.userRegister.jwt;
    // We are passing in the jwt to NextAuth so that we can keep the error handling in our client.
    const result: any = await signIn('sign-in', {
      redirect: false,
      jwt: jwt,
    });
    if (result?.status === 200) {
      if (applyUtmTags) {
        await applyUtmTags(query ?? {}, jwt);
      }
      // Handle creation of GDPR consent.
      if (
        createGdprUserConsent &&
        email &&
        data?.userRegister?.errors.length === 0
      ) {
        createGdprUserConsent(email);
      }

      // At this moment we don't have user information.
      // Next rendered component will use dispatch flag from query string to dispatch GA event.
      const params = new URLSearchParams();
      params.set('dispatch', EVENTS.SIGNUP_START.key);

      if (query?.destination) {
        params.set('destination', query.destination as string);
      }

      if (isApplicant && resumesActive) {
        const resumeWizardTranslatedRoute = getTranslatedLink(
          '/resume/wizard',
          currentLanguage,
        );

        if (sessionStorage.getItem('applyForceRegisterLink')) {
          // Store the apply link as destination so the user is redirected
          // there after completing the resume wizard.
          const applyForceRegisterLink = sessionStorage.getItem(
            'applyForceRegisterLink',
          );
          const safeApplyForceRegisterLink = sanitizeRedirectUrl(
            applyForceRegisterLink ?? '',
          );
          sessionStorage.removeItem('applyForceRegisterLink');
          params.set('destination', safeApplyForceRegisterLink);
        }

        // Always go to resume wizard first.
        window.location.href = buildUrl(
          prefix,
          resumeWizardTranslatedRoute,
          params,
          basePath,
        );
      } else {
        if (isApplicant) {
          // Applicant login but resume disabled go to dashboard.
          window.location.href = buildUrl(
            prefix,
            '/dashboard',
            params,
            basePath,
          );
        } else {
          // If recruiter registered, go to onboarding wizard.
          window.location.href = buildUrl(
            prefix,
            '/organization/wizard',
            params,
            basePath,
          );
        }
      }
    }
  } else {
    // Write a message to the internal store.
    if (data.userRegister.errors.length === 0) {
      messageService.addMessage({
        type: 'success',
        text:
          'Success! you registration was created, an email will be sent to the email provided.',
      });
      redirect('/');
    }
  }
};

const roles = {
  applicant: 'applicant',
  recruiter: 'recruiter',
};

// Updates URL without a refreshing browser window.
const updateURL = (isApplicant: boolean) => {
  Router.push(
    {
      pathname: `/user/register/${
        isApplicant ? roles.applicant : roles.recruiter
      }`,
    },
    undefined,
    { shallow: true },
  );
};

// Returns role if it is the last path part from URL.
const getRoleFromURL = (): string => {
  let urlRole = '';

  [roles.applicant, roles.recruiter].forEach((role) => {
    if (Router.pathname.endsWith(role)) {
      urlRole = role;
    }
  });

  return urlRole;
};

export const Register = () => {
  const frontendConfig = useFrontendConfig();
  const { APP, RESUME, ORGANIZATIONS } = frontendConfig;
  const frontendTheme = getFrontendTheme(frontendConfig);
  const registerImage = getFrontendAssets(frontendConfig).registerImage;

  const {
    createGdprUserConsent,
    isNewsletterChecked,
  } = useGdprConsentContext();
  const appContext: AppContextInterface = useContext(AppContext);
  const router = useRouter();
  const messageService = useMessages();

  const [tabValue, setTabValue] = useState(ROLE_APPLICANT);
  const [isApplicant, setIsApplicant] = useState(
    RESUME.APPLICANT_FEATURE_ACTIVE,
  );
  const [registerUser, { data, loading }] = useMutation(USER_REGISTER);
  const { getConfigByName } = useConfig();
  const CREATION_SOURCE_ENABLED: boolean = !!getConfigByName(
    'jobiqo_creation_source',
  )?.value[0];
  const [utmSource, setUtmSource] = useState<string | null>(null);
  const { applyUtmTags } = useApplyUtmTags();

  // Using useEffect to make sure session storage is available.
  useEffect(() => {
    if (CREATION_SOURCE_ENABLED && sessionStorage.getItem('utm-source')) {
      setUtmSource(sessionStorage.getItem('utm-source'));
    }
  }, []);

  useEffect(() => {
    // Check if URL is /user/register/recruiter
    if (getRoleFromURL() === roles.recruiter) {
      setIsApplicant(false);
      setTabValue(ROLE_RECRUITER);
    }
  }, []);

  const handleTabChange = (value: string) => {
    const activateApplicant = value === ROLE_APPLICANT;
    setIsApplicant(activateApplicant);
    updateURL(activateApplicant);
    setTabValue(value);
  };

  const isSubmittingRegister =
    loading || (data && data.userRegister && data.userRegister.jwt);

  // Define the handleSubmit function
  const onSubmit = (
    formData,
    onComplete,
    isApplicant,
    messageService,
    appContext,
    router,
    createGdprUserConsent,
  ) => {
    if (!APP.READ_ONLY_MODE) {
      registerUser({
        variables: {
          data: {
            email: formData.email,
            password: formData.password,
            ...(APP.NEWSLETTER_FEATURE_ACTIVE
              ? { newsletter: isNewsletterChecked }
              : null),
            role: isApplicant ? ROLE_APPLICANT : ROLE_RECRUITER,
            anonymousUserToken: getAnonymousUserToken(),
            language: appContext.locale,
            additionalData: JSON.stringify(formData),
            ...(CREATION_SOURCE_ENABLED && utmSource
              ? { source: utmSource }
              : null),
            captchaToken: formData.captchaToken,
          },
        },
        refetchQueries: [
          {
            query: GET_USER_INFO,
          },
        ],
      }).then(async (response) => {
        await onComplete(
          response.data,
          isApplicant,
          messageService,
          appContext.locale,
          router.defaultLocale,
          createGdprUserConsent,
          formData.email,
          router.query,
          RESUME.RESUME_FEATURE_ACTIVE,
          APP.BASE_PATH,
          applyUtmTags,
        );
      });
    }
  };

  const loginLink = (
    <Text fontSize={2} textAlign="center" className="registration-login-link">
      <FormattedMessage
        id="register_has_account"
        defaultMessage="Already have an account?"
      />{' '}
      <Link href="/user/login">
        <FormattedMessage id="login" defaultMessage={`Login`} />
      </Link>
    </Text>
  );

  return (
    <UserRegisterLayout registerImage={registerImage}>
      <CenterBox>
        <HeaderLoginPagesSlot />
        <Messages />
        <AlignedCenter flow="column">
          <Spacing size="medium" />
          <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
          <Spacing />
          <HiddenVisually>
            <h1>
              <UserAddIcon color="primary" title="Login" aria-hidden="true" />{' '}
              <FormattedMessage
                id="register_title"
                defaultMessage="Register new account"
              />
            </h1>
          </HiddenVisually>
          {APP.READ_ONLY_MODE && (
            <Spacing marginBottom={6}>
              <MessageAlert alertStyle="danger">
                <RichFormattedMessage
                  id="register_block_readonly"
                  defaultMessage="Registration is blocked temporarily while the site is in maintenance mode."
                />
              </MessageAlert>
            </Spacing>
          )}
          <>
            {data && data.userRegister && data.userRegister.errors.length > 0 && (
              <MessageAlert
                type="assertive"
                alertStyle="danger"
                scrollToMessage={true}
              >
                <ul data-test-id="user-register-errors">
                  {data.userRegister.errors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </ul>
              </MessageAlert>
            )}
            <Grid
              alignItems="start"
              justifyItems="center"
              {...(registerImage ? { columns: [1] } : { columns: [1, 2] })}
            >
              <Spacing paddingTop={[0, 7]}>
                <Cell>
                  {RESUME.APPLICANT_FEATURE_ACTIVE &&
                  ORGANIZATIONS.RECRUITER_FEATURE_ACTIVE ? (
                    <Box padding={0}>
                      <div style={{ maxWidth: '450px', margin: 'auto' }}>
                        <Tabs
                          hardBackground={false}
                          tabsListItemFull={true}
                          value={tabValue}
                          onChange={handleTabChange}
                        >
                          <TabList>
                            <Tab value={ROLE_APPLICANT}>
                              <FormattedMessage
                                id="applicant"
                                defaultMessage="Applicant"
                              />
                            </Tab>
                            <Tab value={ROLE_RECRUITER}>
                              <FormattedMessage
                                id="recruiter"
                                defaultMessage="Recruiter"
                              />
                            </Tab>
                          </TabList>
                          <TabPanel value={ROLE_APPLICANT}>
                            <Spacing />
                            <Text color="gray5" textAlign="center">
                              <FormattedMessage
                                id="applicant_info"
                                defaultMessage="You are registering as an applicant. {break}
                          After registration you will receive a confirmation
                          email"
                                values={{ break: <br /> }}
                              />
                            </Text>
                          </TabPanel>
                          <TabPanel value={ROLE_RECRUITER}>
                            <Spacing />
                            <Text color="gray5" textAlign="center">
                              <FormattedMessage
                                id="recruiter_info"
                                defaultMessage="You are registering as a recruiter. {break}
                          After registration you will receive a confirmation
                          email"
                                values={{ break: <br /> }}
                              />
                            </Text>
                          </TabPanel>
                        </Tabs>
                        <Spacing padding={[6, 8]} paddingTop={[0, 0]}>
                          <RegistrationForm
                            isSubmittingRegister={isSubmittingRegister}
                            onSubmit={(formData) =>
                              onSubmit(
                                formData,
                                onComplete,
                                isApplicant,
                                messageService,
                                appContext,
                                router,
                                createGdprUserConsent,
                              )
                            }
                            isApplicant={isApplicant}
                            frontendConfig={frontendConfig}
                          />
                          <Spacing size="medium" />
                          <div>{loginLink}</div>
                        </Spacing>
                      </div>
                    </Box>
                  ) : (
                    <Box padding={[6, 8]}>
                      <Text tag="h2" textAlign="center" fontSize={4}>
                        <FormattedMessage
                          id="register_title"
                          defaultMessage="Register new account"
                        />
                      </Text>
                      <Spacing size="medium" />
                      <Text color="gray5" textAlign="center">
                        <FormattedMessage
                          id="register_info"
                          defaultMessage="Fill the form below to register. {break}
                After registration you will receive a confirmation
                email"
                          values={{ break: <br /> }}
                        />
                      </Text>
                      <Spacing padding={[5, 8]}>
                        <RegistrationForm
                          isSubmittingRegister={isSubmittingRegister}
                          onSubmit={(formData) =>
                            onSubmit(
                              formData,
                              onComplete,
                              isApplicant,
                              messageService,
                              appContext,
                              router,
                              createGdprUserConsent,
                            )
                          }
                          isApplicant={isApplicant}
                          frontendConfig={frontendConfig}
                        />
                        <Spacing size="medium" />
                        <div>{loginLink}</div>
                      </Spacing>
                    </Box>
                  )}
                </Cell>
              </Spacing>
              {!registerImage && (
                <HideMobile>
                  <Cell>
                    <AlignedCenter>
                      <UserRegisterInfoSlot isApplicant={isApplicant} />
                    </AlignedCenter>
                  </Cell>
                </HideMobile>
              )}
            </Grid>
          </>
        </AlignedCenter>
      </CenterBox>
    </UserRegisterLayout>
  );
};
